import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { requestPlay3 } from '../cdo/cdoPlay3';
import usePlay3Artwork from './usePlay3Artwork';
import usePlay3PortraitDetails from './usePlay3PortraitDetails';
import { localDebugEnabled } from '../shared/localDebug';
import PlaySceneCards from './PlaySceneCards';
import { PlayPlaceCard, PlayPlayerCard, PlayBeingCard } from './PlaySceneCard';
import PlayChatLog from './PlayChatLog';
import PlayActionComposer from './PlayActionComposer';
import PlayActionProgress from './PlayActionProgress';
import { createPlay3Timing } from '../shared/play3Timing';
import ExitGameButton from './ExitGameButton';
import CharacterSheet from './characterSheet';
import LevelAdvancement, { removeDraftAllocationPoint } from './LevelAdvancement';
import { applyConsumptionToInventory } from './playVitals';
import { buildPlaceMapPlan, PlaceMapModal, TradeConfirmationModal } from './Play2';
import { getShapeBounds } from './placeOverviewMap';
import Play3Sky, { Play3SkyDetails } from './Play3Sky';

const dialogueMessages = rows => rows.filter(row => row.kind !== 'narration'
  || (typeof row.text === 'string' && row.text.trim() && row.text.trim().toLowerCase() !== 'null'))
  .map(row => ({ role: row.kind === 'player_input' ? 'user' : row.kind === 'system' ? 'system' : ['narration', 'magic'].includes(row.kind) ? 'assistant' : 'npc',
  speakerId: row.speakerId, speaker: row.publicLabel || 'Unknown person', content: row.text, dialogId: row.id,
  dialogCursor: row.cursor, requestId: row.requestId }));
const withPendingMessage = (messages, pending) => !pending || messages.some(row => row.requestId === pending.requestId && (row.role === 'user' || row.autonomous))
  ? messages : [...messages, { role: pending.autonomous ? 'assistant' : 'user', autonomous: pending.autonomous,
    content: pending.autonomous ? 'Resume the unfinished NPC activity.' : pending.instruction, requestId: pending.requestId,
    dialogId: `pending:${pending.requestId}`, optimistic: true }];
const mergeDialogueMessages = (current, rows, requestId) => {
  const saved = dialogueMessages(rows).map(row => ({ ...row, requestId: row.requestId || requestId }));
  const updates = new Map(saved.map(row => [row.dialogId, row]));
  const playerInputs = new Map(saved.filter(row => row.role === 'user' && row.requestId).map(row => [row.requestId, row]));
  // Replace the immediate echo in place, so streamed replies keep their order.
  // The request identity also distinguishes separate identical instructions.
  const merged = current.map(row => row.optimistic && playerInputs.has(row.requestId)
    ? playerInputs.get(row.requestId) : updates.get(row.dialogId) || row);
  const known = new Set(merged.map(row => row.dialogId));
  return [...merged, ...saved.filter(row => !known.has(row.dialogId))];
};
const intentReturnValues = result => {
  const source = result.intentResult || result;
  if (!Array.isArray(source.intents)) return null;
  const keys = ['actions', 'intents', 'travel', 'actionScope', 'scope', 'moveTo', 'contextRequests', ...(result.intentResult ? ['movement', 'search'] : [])];
  return Object.fromEntries(keys.filter(key => source[key] !== undefined).map(key => [key, source[key]]));
};
const mapStyles = {
  road: { color: '#cbd5e1', label: 'Road' }, water: { color: '#62b1e8', label: 'Water' },
  wall: { color: '#cfba86', label: 'Walls / gates' }, building: { color: '#c4b5fd', label: 'Buildings' },
  boundary: { color: '#6985a1', label: 'Settlement boundary' },
};
const mapKind = entry => entry.mapRole === 'Road' ? 'road' : ['River', 'Water'].includes(entry.mapRole) ? 'water'
  : /^(fortification|wall|fence)$/i.test(entry.placeType || '') ? 'wall' : 'building';

export const stylePlay3MapPlan = (plan, mapPlaces, settlement) => {
  if (!plan) return plan;
  const byId = new Map(mapPlaces.map(entry => [entry.idEntity, entry]));
  const nodes = plan.nodes.map(node => {
    const entry = byId.get(node.id), kind = entry && mapKind(entry);
    // Physical corridor widths scale with the map. Building outlines and the
    // pointer hit areas keep their existing screen-sized strokes.
    const physical = kind === 'road' || kind === 'water';
    const layered = { ...node, ...(kind === 'water' ? { renderLayer: 1 } : {}), scaleStroke: physical };
    if (!settlement) return layered;
    if (node.isFocus) return { ...layered, fill: 'none', stroke: mapStyles.boundary.color, strokeWidth: 1, hoverable: false };
    if (!entry) return layered;
    const bounds = getShapeBounds(entry), scale = node.width / (bounds.maxX - bounds.minX || 1);
    return { ...layered, fill: 'none', stroke: mapStyles[kind].color,
      strokeWidth: Math.max(physical ? 2 : 1.4, (entry.shape.shapeData.width || .5) * scale) };
  }).sort((left, right) => left.renderLayer - right.renderLayer);
  // A dark route casing erased thin bridges beneath recorded movement.
  return { ...plan, nodes, traversalCasing: false };
};

export default function Play3({ playerId, onExitGame, onBootstrap, characterSheetProps }) {
  const canUseDebug = localDebugEnabled();
  const [bootstrap, setBootstrap] = useState(null);
  const [portraitDetailsRevision, setPortraitDetailsRevision] = useState(0);
  const portraitRefreshes = useRef(new Set());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [preserveComposerDraft, setPreserveComposerDraft] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [progress, setProgress] = useState(null);
  const [turnComplete, setTurnComplete] = useState(false);
  const [movementPreview, setMovementPreview] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [messages, setMessages] = useState([]);
  const [intentResult, setIntentResult] = useState(null);
  const [debugOpen, setDebugOpen] = useState(false);
  const [failedRequest, setFailedRequest] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [allocationDraft, setAllocationDraft] = useState([]);
  const [allocating, setAllocating] = useState(false);
  const [allocationError, setAllocationError] = useState('');
  const allocationRequest = useRef(null);
  const skillProgression = bootstrap?.skillProgression || bootstrap?.character?.skillProgression;
  const advancementPending = skillProgression?.phase === 'allocationRequired';
  useEffect(() => {
    setAllocationDraft([]); setAllocationError(''); allocationRequest.current = null;
  }, [playerId, skillProgression?.transition?.id, skillProgression?.revision]);
  const [mapId, setMapId] = useState('');
  const [zoom, setZoom] = useState(1);
  const [historyBefore, setHistoryBefore] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const pendingRequest = useRef(null);
  const pendingTurn = useRef(null);
  const actionGeneration = useRef(0);
  const movementSequence = useRef(0), committedMovement = useRef(null);
  const historyScroll = useRef(null);
  const timing = useRef(null);
  const artworkReady = Boolean(bootstrap);
  const artworkPlaces = [...new Set([...(bootstrap?.places || []), ...(bootstrap?.visiblePlaces || []), ...(bootstrap?.containingPlaces || [])]
    .map(place => place.idEntity))].sort().join('|');
  const artworkBeings = [...new Set([...(bootstrap?.beings || []), ...(mapId ? bootstrap?.mapBeings || [] : [])]
    .map(being => being.idEntity))].sort().join('|');
  const artworkPortraits = JSON.stringify((bootstrap?.beings || []).map(being => [being.idEntity, being.portraitMediaId || null, being.portraitVersion || '']).sort((a, b) => a[0].localeCompare(b[0])));
  const { places: images, beings: npcPortraits, portrait } = usePlay3Artwork(playerId, artworkReady, artworkPlaces, artworkBeings, artworkPortraits);
  const bootstrapCallback = useRef(onBootstrap), chatLog = useRef(null), inFlight = useRef(false);
  const referenceCatalog = useRef({});
  bootstrapCallback.current = onBootstrap;
  const loadPortraitRelationships = usePlay3PortraitDetails(playerId, bootstrap?.session?.sessionId, portraitDetailsRevision);

  useEffect(() => {
    const controller = new AbortController();
    setBootstrap(null); setMovementPreview(null); committedMovement.current = null;
    portraitRefreshes.current.clear();
    setError(''); setLoading(true); setMessages([]); setFailedRequest(null); setTurnComplete(false);
    setIntentResult(null); setDebugOpen(false);
    setHistoryBefore(null); historyScroll.current = null; pendingRequest.current = null; pendingTurn.current = null;
    const load = async () => {
      let result = await requestPlay3(playerId, '/bootstrap', { signal: controller.signal });
      // Opening also idempotently establishes scene knowledge and first-contact
      // rapport for an existing character. The GET bootstrap itself stays read-only.
      result = { ...result, ...await requestPlay3(playerId, '/session/open', { method: 'POST', signal: controller.signal }) };
      if (controller.signal.aborted) return;
      committedMovement.current = result.session;
      setBootstrap(result);
      setPortraitDetailsRevision(current => current + 1);
      resourceCharacter.current = result.character;
      referenceCatalog.current = result.references;
      pendingRequest.current = result.pendingTurn || null;
      pendingTurn.current = result.pendingTurn || null;
      setFailedRequest(result.pendingTurn || null);
      setMessages(withPendingMessage(dialogueMessages(result.dialogue?.messages || []), result.pendingTurn));
      setHistoryBefore(result.dialogue?.before || null);
      bootstrapCallback.current?.({ player: result.character, references: result.references });
    };
    load().catch(failure => { if (!controller.signal.aborted) setError(failure.message); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [playerId, attempt]);

  useEffect(() => {
    // Streamed NPC replies and progress controls can change layout together.
    // Read the final height on the next frame, and again when the turn finishes.
    const frame = window.requestAnimationFrame(() => {
      if (!chatLog.current) return;
      const previous = historyScroll.current;
      chatLog.current.scrollTop = previous
        ? Math.max(0, previous.top + chatLog.current.scrollHeight - previous.height) : chatLog.current.scrollHeight;
      historyScroll.current = null;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages, submitting]);

  useEffect(() => {
    const trace = timing.current;
    if (!trace) return;
    if (messages.some(row => row.requestId === trace.turnId && row.role === 'user')) trace.rendered('instruction_rendered');
    if (messages.some(row => row.requestId === trace.turnId && row.role === 'npc')) trace.rendered('first_reply_rendered');
    if (!submitting) { trace.finish(trace.status || 'complete'); timing.current = null; }
  }, [messages, submitting]);

  const resourceCharacter = useRef(null);
  const applyUpdate = useCallback((result, requestId) => {
    // Streamed receipts repeat session/dialogue fields many times. They are not
    // separate portrait revisions. Refresh once per completed turn/exchange.
    const refreshKeys = [result.turn?.complete && `turn:${result.turn.requestId || requestId}`,
      result.commerceResolution?.confirmationId && `trade:${result.commerceResolution.confirmationId}`].filter(Boolean);
    if (refreshKeys.some(key => !portraitRefreshes.current.has(key))) {
      refreshKeys.forEach(key => portraitRefreshes.current.add(key));
      setPortraitDetailsRevision(current => current + 1);
    }
    if (result.session) {
      committedMovement.current = result.session;
      setMovementPreview(current => current && result.session.sessionId === current.sessionId
        && result.session.scene.minute < current.minute && (result.session.movementId || null) === current.baseMovementId ? current : null);
    }
    if (result.character) resourceCharacter.current = result.character;
    if (resourceCharacter.current && result.actionOutcome?.intent === 'consume' && result.actionOutcome.status === 'completed') {
      resourceCharacter.current = applyConsumptionToInventory(resourceCharacter.current, result.actionOutcome);
      result = { ...result, character: resourceCharacter.current };
    }
    if (result.resources && resourceCharacter.current) {
      const fields = ['currentHp', 'maxHp', 'currentPp', 'maxPp', 'currentStamina', 'maxStamina', 'hunger', 'thirst', 'intoxication'];
      const values = Object.fromEntries(fields.filter(key => Number.isFinite(result.resources[key])).map(key => [key, result.resources[key]]));
      result = { ...result, character: { ...resourceCharacter.current, ...values } };
      resourceCharacter.current = result.character;
    }
    const updatedPendingTurn = result.turn && !result.turn.complete
      ? { requestId: result.turn.requestId, instruction: result.turn.instruction, ...(result.turn.execution ? { execution: result.turn.execution } : {}) } : null;
    if (result.turn) {
      setTurnComplete(result.turn.complete === true && !result.turn.cancelled);
      pendingTurn.current = updatedPendingTurn;
      if (pendingTurn.current) pendingRequest.current = pendingTurn.current;
      else {
        const completedId = result.turn.requestId || requestId;
        if (pendingRequest.current?.requestId === completedId) pendingRequest.current = null;
        setFailedRequest(current => current?.requestId === completedId ? null : current);
      }
    }
    if (result.character) bootstrapCallback.current?.({ player: result.character, references: referenceCatalog.current });
    const intent = intentReturnValues(result);
    if (intent) setIntentResult(intent);
    if (result.dialogue) setMessages(current => mergeDialogueMessages(current, result.dialogue, result.turn?.requestId || requestId));
    setBootstrap(current => {
      if (!current) return current;
      const presentation = result.presentation;
      const nextSession = result.session || current.session;
      const environment = result.environment || presentation?.environment;
      let mapPlaces = current.mapPlaces || [], mapState = current.mapState;
      let acceptMap = false;
      const update = result.mapUpdate;
      if (update && (update.mapAreaId !== mapState?.mapAreaId || update.revision >= (mapState?.revision || 0))) {
        acceptMap = true;
        const byId = new Map((update.mapAreaId === mapState?.mapAreaId ? mapPlaces : []).map(row => [row.idEntity, row]));
        for (const row of update.upsert) byId.set(row.idEntity, row);
        mapPlaces = [...byId.values()]; mapState = { mapAreaId: update.mapAreaId, revision: update.revision };
      } else if (result.mapPlaces && (!result.mapState || result.mapState.mapAreaId !== mapState?.mapAreaId
        || result.mapState.revision >= (mapState?.revision || 0))) {
        acceptMap = true;
        mapPlaces = result.mapPlaces; mapState = result.mapState;
      }
      let combat = current.combat;
      if (Object.hasOwn(result, 'combat') || result.movement?.combat?.status === 'ended') {
        const candidate = result.combat?.status === 'active' ? result.combat : null;
        const candidateRevision = result.combat?.revision ?? result.movement?.combat?.roundNumber ?? 0;
        const currentRevision = combat?.revision ?? combat?.roundNumber ?? 0;
        if (candidate) {
          if (!combat || candidate.encounterId !== combat.encounterId || candidateRevision >= currentRevision) combat = candidate;
        } else {
          const endedEncounterId = result.combat?.encounterId || result.movement?.combat?.encounterId;
          if (!combat || !endedEncounterId || (endedEncounterId === combat.encounterId && candidateRevision >= currentRevision)) combat = null;
        }
      }
      return { ...current,
        ...(environment && Math.abs(environment.minute - nextSession.scene.minute) < 1e-7 ? { environment } : {}),
        ...(Object.hasOwn(result, 'continuationPending') ? { continuationPending: result.continuationPending } : {}),
        ...(Object.hasOwn(result, 'pendingTradeConfirmation') ? { pendingTradeConfirmation: result.pendingTradeConfirmation } : {}),
        ...(Object.hasOwn(result, 'commerceResolution') ? { commerceResolution: result.commerceResolution } : {}),
        ...(result.turn ? { actionOutcome: result.actionOutcome || null } : {}),
        ...((Object.hasOwn(result, 'combat') || result.movement?.combat?.status === 'ended') ? { combat } : {}),
        ...(result.character ? { character: result.character } : {}),
        ...(result.skillProgression ? { skillProgression: result.skillProgression } : {}),
        pendingTurn: result.turn ? updatedPendingTurn : current.pendingTurn,
        places: (result.places || current.places).map(place => presentation?.placeNames[place.idEntity] ? { ...place, name: presentation.placeNames[place.idEntity] } : place),
        visiblePlaces: (result.visiblePlaces || current.visiblePlaces || []).map(place => presentation?.placeNames[place.idEntity]
          ? { ...place, name: presentation.placeNames[place.idEntity] } : place),
        containingPlaces: (result.containingPlaces || current.containingPlaces || []).map(place => presentation?.placeNames[place.idEntity]
          ? { ...place, name: presentation.placeNames[place.idEntity] } : place),
        mapPlaces: mapPlaces.map(place => presentation?.placeNames[place.idEntity]
          ? { ...place, name: presentation.placeNames[place.idEntity] } : place), mapState,
        ...(acceptMap && Object.hasOwn(result, 'mapBeings') ? { mapBeings: result.mapBeings } : {}),
        ...(acceptMap && Object.hasOwn(result, 'recentTraversal') ? { recentTraversal: result.recentTraversal } : {}),
        beings: (result.beings || current.beings || []).map(being => presentation?.beingNames[being.idEntity]
          ? { ...being, name: presentation.beingNames[being.idEntity], publicLabel: presentation.beingNames[being.idEntity], nameKnown: true } : being),
        session: { ...nextSession, scene: { ...nextSession.scene, ...(presentation ? { label: presentation.sceneLabel } : {}) } },
      };
    });
  }, []);

  useEffect(() => {
    if (!(bootstrap?.continuationPending ?? bootstrap?.session?.pendingWork?.automatic) || submitting) return undefined;
    const controller = new AbortController();
    let stopped = false, timer;
    const poll = async () => {
      const generation = actionGeneration.current;
      try {
        const after = messages.reduce((latest, row) => {
          const candidate = String(row.dialogCursor || row.dialogId || '').replace(/^0+(?=\d)/, '');
          return /^[0-9]+$/.test(candidate) && (candidate.length > latest.length
            || (candidate.length === latest.length && candidate > latest)) ? candidate : latest;
        }, '0');
        const session = bootstrap.session;
        const executionRequest = bootstrap.pendingTurn?.requestId;
        const result = await requestPlay3(playerId, `/events?after=${after}${executionRequest ? `&requestId=${encodeURIComponent(executionRequest)}` : ''}&sessionId=${encodeURIComponent(session.sessionId)}&movementId=${encodeURIComponent(session.movementId || '')}&minute=${encodeURIComponent(session.scene.minute)}`, { signal: controller.signal });
        // A response that overlapped a new action may deliver saved messages,
        // but cannot replace that newer action's scene or pending-turn state.
        if (!stopped && !inFlight.current && generation === actionGeneration.current && !result.obsolete && result.sceneVersion?.sessionId === session.sessionId
          && result.sceneVersion.minute >= session.scene.minute
          && ((result.turn && (result.turn.complete || result.turn.execution?.status !== bootstrap.pendingTurn?.execution?.status)) || result.dialogue?.length || result.session?.movementId !== session.movementId || result.session?.scene.minute > session.scene.minute
            || result.continuationPending !== bootstrap.continuationPending
            || JSON.stringify(result.session?.pendingWork) !== JSON.stringify(session.pendingWork))) applyUpdate(result);
      } catch (error) { if (error.name === 'AbortError') return; }
      if (!stopped) timer = window.setTimeout(poll, 2000);
    };
    timer = window.setTimeout(poll, 500);
    return () => { stopped = true; controller.abort(); window.clearTimeout(timer); };
  }, [playerId, bootstrap?.session, bootstrap?.continuationPending, bootstrap?.pendingTurn?.requestId,
    bootstrap?.pendingTurn?.execution?.status, messages, submitting, applyUpdate]);

  const submit = async (instruction, control, { preserveDraft = false } = {}) => {
    if (inFlight.current || loading || advancementPending || !bootstrap || (bootstrap.pendingTradeConfirmation && !bootstrap.pendingTurn) || !instruction.trim()) return;
    if (pendingTurn.current && pendingTurn.current.instruction !== instruction) {
      setError(`Resume the saved turn before submitting a different action or question.${failedRequest?.errorMessage ? ` ${failedRequest.errorMessage}` : ''}`);
      return;
    }
    const generation = ++actionGeneration.current;
    inFlight.current = true; movementSequence.current = 0; setMovementPreview(null);
    setPreserveComposerDraft(preserveDraft); setSubmitting(true); setProgress(null); setError(''); setFailedRequest(null); setTurnComplete(false);
    if (pendingRequest.current?.instruction !== instruction) pendingRequest.current = { instruction, requestId: crypto.randomUUID() };
    const requestId = pendingRequest.current.requestId;
    const attemptedRequest = { instruction, requestId, ...(control ? { control } : {}) };
    timing.current = createPlay3Timing(requestId);
    const trace = timing.current;
    let completed = false;
    setMessages(current => current.some(row => row.role === 'user' && row.requestId === requestId) ? current
      : [...current, { role: 'user', content: instruction, dialogId: `pending:${requestId}`, requestId, optimistic: true }]);
    try {
      const response = await requestPlay3(playerId, '/respond', {
        timing: trace,
        method: 'POST', data: { instruction, requestId, ...(control ? { control } : {}) },
        onEvent: event => {
          if (generation !== actionGeneration.current) return;
          if (event.type === 'progress') setProgress(event.progress);
          if (event.type === 'movement') {
            const preview = event.preview, committed = committedMovement.current || bootstrap.session;
            if (preview.requestId === requestId && preview.sessionId === committed.sessionId
              && preview.mapAreaId === committed.scene.placeId && preview.baseMinute >= committed.scene.minute
              && preview.baseMovementId === (committed.movementId || null) && preview.sequence > movementSequence.current) {
              movementSequence.current = preview.sequence;
              setMovementPreview(current => {
                const seen = new Map((current?.beings || []).map(being => [being.idEntity, { ...being, visibility: 'remembered' }]));
                for (const being of preview.beings) seen.set(being.idEntity, being);
                return { ...preview, beings: [...seen.values()] };
              });
            }
          }
          if (event.type === 'update') {
            if (event.result.turn?.complete === true) completed = true;
            applyUpdate(event.result, requestId);
          }
        },
      });
      if (!response.dialogue) setMessages(current => [...current,
        ...(response.speech?.responses || []).filter(row => row.verbalResponse).map(row => ({ role: 'npc', speaker: row.publicLabel, content: row.verbalResponse }))]);
      applyUpdate(response, requestId);
      if (response.turn?.complete !== false) {
        setTurnComplete(true);
        // Legacy saved replies may omit turn metadata. A successful terminal
        // response still releases both the UI projection and its guard ref.
        pendingTurn.current = null; pendingRequest.current = null;
        setBootstrap(current => ({ ...current, pendingTurn: null }));
        setFailedRequest(null);
      } else setFailedRequest(pendingTurn.current);
    } catch (failure) {
      setMovementPreview(null);
      trace.status = 'failed'; trace.mark('failure_received');
      setError(failure.message);
      const hadSavedTurn = pendingTurn.current?.requestId === requestId;
      // A saved-turn update may itself be stale after a transport failure.
      // Refresh durable status even when the browser already has a pending ref.
      try {
        const recovered = await requestPlay3(playerId, '/bootstrap', { timing: trace });
        if (Object.hasOwn(recovered, 'pendingTurn')) {
          const recoveredPending = recovered.pendingTurn || null;
          pendingTurn.current = recoveredPending;
          setBootstrap(current => ({ ...current, pendingTurn: recoveredPending }));
          if (recovered.pendingTurn) {
            pendingRequest.current = recovered.pendingTurn;
            if (recovered.pendingTurn.requestId !== requestId)
              setMessages(current => current.filter(row => !(row.optimistic && row.requestId === requestId)));
          } else if ((completed || hadSavedTurn) && recovered.session?.sessionId === bootstrap.session.sessionId) {
            applyUpdate({ ...recovered, dialogue: recovered.dialogue?.messages || [] }, requestId);
            setTurnComplete(true);
            pendingRequest.current = null; setFailedRequest(null); setError('');
            return;
          }
        }
      } catch { /* Keep the original failure and request ID for an explicit retry. */ }
      const retained = pendingRequest.current || attemptedRequest;
      const retry = { ...retained, ...(control && retained.requestId === requestId ? { control } : {}), errorMessage: failure.message };
      pendingRequest.current = retry;
      setFailedRequest(retry);
      setMessages(current => withPendingMessage(current, retry));
    }
    finally { inFlight.current = false; setSubmitting(false); setProgress(null); setMovementPreview(null); }
  };

  const stopTurn = async () => {
    if (inFlight.current || !failedRequest || !bootstrap?.session) return;
    const request = failedRequest;
    inFlight.current = true; setSubmitting(true); setPreserveComposerDraft(true); setError('');
    setTurnComplete(false); actionGeneration.current++;
    try {
      const result = await requestPlay3(playerId, '/respond', { method: 'POST', data: { control: 'stop_turn',
        sessionId: bootstrap.session.sessionId, requestId: request.requestId } });
      applyUpdate(result, request.requestId);
      pendingTurn.current = null; pendingRequest.current = null; setFailedRequest(null);
      setBootstrap(current => ({ ...current, pendingTurn: null }));
      if (result.turn?.cancelled) setMessages(current => [...current, { role: 'system',
        content: 'Turn stopped. Completed progress was kept.', dialogId: `stopped:${request.requestId}` }]);
    } catch (failure) { setError(failure.message); }
    finally { inFlight.current = false; setSubmitting(false); setProgress(null); setMovementPreview(null); }
  };

  const stopTravel = async () => {
    if (inFlight.current || !bootstrap?.session?.pendingMovement?.travel) return;
    inFlight.current = true; setSubmitting(true); setError(''); actionGeneration.current++;
    try {
      const result = await requestPlay3(playerId, '/respond', { method: 'POST', data: { control: 'stop',
        sessionId: bootstrap.session.sessionId, movementId: bootstrap.session.movementId } });
      applyUpdate(result);
    } catch (failure) { setError(failure.message); }
    finally { inFlight.current = false; setSubmitting(false); }
  };

  const loadEarlier = async () => {
    if (loadingHistory || !historyBefore) return;
    setLoadingHistory(true); setError('');
    try {
      const page = await requestPlay3(playerId, `/dialogue?before=${encodeURIComponent(historyBefore)}`);
      if (chatLog.current) historyScroll.current = { top: chatLog.current.scrollTop, height: chatLog.current.scrollHeight };
      setMessages(current => {
        const known = new Set(current.map(row => row.dialogId));
        return [...dialogueMessages(page.messages).filter(row => !known.has(row.dialogId)), ...current];
      });
      setHistoryBefore(page.before);
    } catch (failure) { setError(failure.message); }
    finally { setLoadingHistory(false); }
  };

  const resetGame = async () => {
    actionGeneration.current++;
    await requestPlay3(playerId, '/reset', { method: 'POST', data: { sessionId: bootstrap.session.sessionId } });
    setAttempt(value => value + 1);
  };

  const resolveCommerce = async accept => {
    actionGeneration.current++;
    const pending = bootstrap?.pendingTradeConfirmation;
    if (inFlight.current || !pending) return;
    inFlight.current = true; setConfirming(true); setError('');
    try {
      const result = await requestPlay3(playerId, '/commerce/confirm', { method: 'POST', data: {
        sessionId: bootstrap.session.sessionId, confirmationId: pending.confirmationId, accept,
      } });
      applyUpdate({ ...result, dialogue: result.dialogue.messages });
    } catch (failure) {
      // Confirmation may have committed before its response connection failed.
      // Read the exact durable outcome; never repeat or infer the exchange.
      try {
        const recovered = await requestPlay3(playerId, '/bootstrap');
        if (recovered.session?.sessionId === bootstrap.session.sessionId
          && recovered.commerceResolution?.confirmationId === pending.confirmationId
          && recovered.commerceResolution.accepted === accept) {
          applyUpdate({ ...recovered, dialogue: recovered.dialogue?.messages || [] });
        } else setError(failure.message);
      } catch { setError(failure.message); }
    }
    finally { inFlight.current = false; setConfirming(false); }
  };

  const player = bootstrap?.character;
  const saveAllocation = async () => {
    if (allocating || inFlight.current) return;
    allocationRequest.current ||= { sessionId: bootstrap.session.sessionId,
      idempotencyKey: window.crypto.randomUUID(), transitionId: skillProgression.transition.id, revision: skillProgression.revision,
      allocations: [...new Set(allocationDraft.filter(point => point.kind === 'skill').map(point => point.id))]
        .map(id => ({ primarySkillId: id, points: allocationDraft.filter(point => point.kind === 'skill' && point.id === id).length })),
      statAllocations: allocationDraft.filter(point => point.kind === 'stat').map(point => point.id) };
    // An older background poll cannot restore pre-allocation skill choices.
    actionGeneration.current++; inFlight.current = true;
    setAllocating(true); setAllocationError('');
    try { applyUpdate(await requestPlay3(playerId, '/allocate-progression', { method: 'POST', data: allocationRequest.current })); }
    catch (failure) { setAllocationError(failure.message || 'Could not save the allocation.'); }
    finally { inFlight.current = false; setAllocating(false); }
  };
  const scene = bootstrap?.session?.scene;
  const frameIds = new Set((bootstrap?.places || []).map(entry => entry.idEntity));
  const contextCards = [...(bootstrap?.places || []).filter(entry => entry.containsPlayer !== false), ...(bootstrap?.containingPlaces || [])];
  const nearbyCards = [...(bootstrap?.places || []).filter(entry => entry.containsPlayer === false), ...(bootstrap?.visiblePlaces || [])]
    .map(entry => ({ kind: 'place', entry, distance: entry.distanceFeet ?? Infinity }))
    .concat((bootstrap?.beings || []).map(entry => ({ kind: 'being', entry,
      distance: entry.location && scene ? Math.hypot(entry.location.x - scene.position.x, entry.location.z - scene.position.z) : Infinity })))
    .sort((a, b) => a.distance - b.distance || a.entry.idEntity.localeCompare(b.entry.idEntity));
  const placeCard = entry => <PlayPlaceCard key={entry.idEntity} placeId={entry.idEntity}
    label={entry.placeType?.toLowerCase() === 'world' ? 'World Map' : entry.name} imageState={images[entry.idEntity]}
    showImage={!frameIds.has(entry.idEntity)}
    onOpenMap={id => { setMapId(id); setZoom(/^(world|continent|nation|country|region)$/i.test(entry.placeType) ? 20 : 1); }} />;
  const place = bootstrap?.places.find(entry => entry.idEntity === mapId);
  // The reused map planner needs bounds for multipart visible paths. Without
  // them it falls back to (0,0), making a settlement at world coordinates tiny.
  const mapPlaces = useMemo(() => (bootstrap?.mapPlaces || []).map(entry => ({ ...entry,
    shape: { ...entry.shape, ...getShapeBounds(entry) } })), [bootstrap?.mapPlaces]);
  const mapScene = useMemo(() => movementPreview && scene
    ? { ...scene, position: movementPreview.position, minute: movementPreview.minute } : scene, [movementPreview, scene]);
  const mapBeings = useMemo(() => {
    const saved = bootstrap?.mapBeings || bootstrap?.beings || [];
    if (!movementPreview) return saved;
    const seen = new Map(saved.map(being => [being.idEntity, { ...being, visibility: 'remembered' }]));
    for (const being of movementPreview.beings) seen.set(being.idEntity, { ...seen.get(being.idEntity), ...being });
    return [...seen.values()];
  }, [bootstrap?.mapBeings, bootstrap?.beings, movementPreview]);
  const mapTraversal = useMemo(() => {
    if (!movementPreview?.paths.length) return bootstrap?.recentTraversal;
    const previous = bootstrap?.recentTraversal;
    return { journeyId: movementPreview.journeyId,
      paths: [...(previous?.journeyId === movementPreview.journeyId ? previous.paths : []), ...movementPreview.paths] };
  }, [bootstrap?.recentTraversal, movementPreview]);
  const mapPlan = useMemo(() => {
    if (!place || !mapScene) return null;
    const plan = buildPlaceMapPlan({
      // Regional roads remain drawable infrastructure, but their long extents
      // must not shrink the settlement when its card first opens the map.
      focusPlace: place.idEntity === scene.placeId ? { ...place,
        composedOfChildren: mapPlaces.filter(entry => !['road', 'water'].includes(mapKind(entry))) } : place,
      places: [...bootstrap.places, ...mapPlaces],
      beings: mapBeings.map(being => ({ ...being,
        name: bootstrap.presentation?.beingNames[being.idEntity] || being.publicLabel || being.name || 'Unknown person' })),
      recentTraversal: mapTraversal,
      explorerLocation: { placeId: mapScene.placeId, location: mapScene.position },
      knownMapPlaceIds: [...bootstrap.places, ...mapPlaces].map(entry => entry.idEntity) });
    return stylePlay3MapPlan(plan, mapPlaces, place.idEntity === scene.placeId);
  }, [place, scene, bootstrap, mapPlaces, mapScene, mapBeings, mapTraversal]);
  const placeLabel = entry => [...(bootstrap?.places || []), ...(bootstrap?.mapPlaces || [])].find(candidate => candidate.idEntity === entry.idEntity)?.name || 'Place';
  const minute = scene?.minute ?? 600;
  const clockMinute = Math.floor(minute);
  const time = `${String(Math.floor(clockMinute / 60) % 24).padStart(2, '0')}:${String(clockMinute % 60).padStart(2, '0')}`;
  const attackingActorIds = new Set(bootstrap?.combat?.attackingActorIds || []);

  return <main className="play2-page play3-page" aria-label="Play3">
    <Play3Sky environment={bootstrap?.environment || bootstrap?.presentation?.environment} />
    <PlaySceneCards>
      {contextCards.map(placeCard)}
      {player && <PlayPlayerCard label={player.name} portrait={portrait} health={player.currentHp ?? 100} maxHealth={player.maxHp ?? 100}
        stamina={player.currentStamina ?? 100} maxStamina={player.maxStamina ?? 100} mana={player.currentPp ?? 0} maxMana={player.maxPp}
        hasMana={Number(player.maxPp) > 0} advancementPending={advancementPending}
        attacking={attackingActorIds.has(player.idEntity)}
        onOpenSheet={characterSheetProps ? () => setSheetOpen(true) : undefined} />}
      {nearbyCards.map(({ kind, entry }) => kind === 'place' ? placeCard(entry)
        : <PlayBeingCard key={entry.idEntity} label={entry.publicLabel || entry.name || 'Unknown person'}
          beingId={entry.idEntity} portrait={npcPortraits[entry.idEntity]} loadRelationships={loadPortraitRelationships}
          attacking={attackingActorIds.has(entry.idEntity)} />)}
    </PlaySceneCards>
    <section className="play-conversation-panel play2-conversation-panel">
      <div className="play-status-stack">
        {loading && <div className="panel-status" role="status">Starting...</div>}
        {!loading && !bootstrap && <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>}
        {scene && <div className="play-session-meta play2-session-meta">
          <div className="panel-status play-time-status">Time of day: {time}</div>
          {bootstrap?.combat?.status === 'active' && <div className="panel-status" role="status">Combat — round {bootstrap.combat.roundNumber}</div>}
          {canUseDebug && <button type="button" className="secondary-button play2-debug-button" onClick={() => setDebugOpen(true)}>Debug View</button>}
          <Play3SkyDetails environment={bootstrap?.environment || bootstrap?.presentation?.environment} />
        </div>}
      </div>
      <PlayChatLog messages={messages} logRef={chatLog} emptyText={loading ? 'Starting...' : 'No conversation yet.'}
        historyControl={historyBefore && <button type="button" className="secondary-button" style={{ justifySelf: 'start', alignSelf: 'start' }}
          onClick={loadEarlier} disabled={loadingHistory}>{loadingHistory ? 'Loading dialogue...' : 'Load earlier dialogue'}</button>}
        speakerLabel={label => label} renderMessageActions={message => failedRequest && (message.role === 'user' || message.autonomous) && message.requestId === failedRequest.requestId
          ? <div className="button-row play-submit-row"><button type="button" className="secondary-button"
            disabled={loading || submitting || confirming || advancementPending}
            onClick={() => submit(failedRequest.instruction, failedRequest.control, { preserveDraft: true })}>{failedRequest.execution ? 'Resume action' : 'Complete Turn'}</button>
            <button type="button" className="secondary-button" disabled={loading || submitting || confirming}
              onClick={stopTurn}>Stop Turn</button></div> : null} />
      <div className="play-composer">
        {bootstrap?.session?.pendingMovement?.travel && !bootstrap.pendingTurn && <div className="button-row play-submit-row" style={{ gap: '8px' }}>
          <button type="button" className="secondary-button" disabled={loading || submitting || confirming || advancementPending}
            onClick={() => submit('Continue the travel.', 'continue', { preserveDraft: true })}>Continue</button>
          <button type="button" className="secondary-button" disabled={loading || submitting || confirming} onClick={stopTravel}>Stop</button>
        </div>}
        {error && <div className="panel-status error-status" role="alert">{error}</div>}
        <PlayActionComposer key={`${playerId}:${bootstrap?.session?.sessionId || ''}`}
          draftOwnerId={`${playerId}:${bootstrap?.session?.sessionId || ''}`} isLoading={loading} isSubmitting={submitting}
          preserveDraftOnSubmit={preserveComposerDraft}
          inputDisabled={advancementPending}
          status={<PlayActionProgress active={submitting} completed={turnComplete && !error && !failedRequest} progress={progress} keepVisible compact />}
          onSubmit={submit} disabled={!bootstrap || loading || confirming || advancementPending || Boolean(bootstrap.pendingTradeConfirmation && !bootstrap.pendingTurn)}
          actions={<span className="play-game-controls">{onExitGame && <ExitGameButton onExit={onExitGame} />}
            <ExitGameButton onExit={resetGame} label="Reset Game" title="Reset game?"
              message="Erase this character's gameplay knowledge, rapport, and associated events and dialogue, then restart at 10:00 on the southern road? Your character and items will be kept."
              failureMessage="Unable to reset Play3. Please try again." disabled={!scene || loading || submitting || confirming || loadingHistory} />
          </span>} />
      </div>
    </section>
    {!submitting && !bootstrap?.pendingTurn && <TradeConfirmationModal pendingTradeConfirmation={bootstrap?.pendingTradeConfirmation}
      onConfirm={() => resolveCommerce(true)} onCancel={() => resolveCommerce(false)} isSubmitting={confirming} />}
    {canUseDebug && debugOpen && <div className="play2-debug-overlay" role="dialog" aria-modal="true" aria-label="Play3 debug view">
      <div className="play2-debug-modal">
        <div className="play2-debug-header">
          <h3>Debug View</h3>
          <button type="button" className="secondary-button" onClick={() => setDebugOpen(false)}>Close</button>
        </div>
        <section className="play2-debug-section">
          <h4>Intent return values</h4>
          {intentResult ? <pre aria-label="Intent and context JSON">{JSON.stringify(intentResult, null, 2)}</pre>
            : <p>No intent result yet.</p>}
        </section>
      </div>
    </div>}
    {sheetOpen && characterSheetProps && <div className="play-character-sheet-overlay" role="dialog" aria-modal="true" aria-label="Character sheet">
      <div className="play-character-sheet-modal">
        <button type="button" className="play-character-sheet-close" disabled={allocating} onClick={() => setSheetOpen(false)} aria-label="Close character sheet">Close</button>
        <CharacterSheet {...characterSheetProps} portrait={portrait.src ? { ...characterSheetProps.portrait, src: portrait.src, isSaved: true } : characterSheetProps.portrait}
          value={{ ...characterSheetProps.value, ...player }} initialTab={advancementPending ? 'stats' : undefined}
          progression={skillProgression}
          advancement={skillProgression && <LevelAdvancement character={{ ...characterSheetProps.value, ...player }} progression={skillProgression}
            primarySkillOptions={characterSheetProps.primarySkillOptions} draft={allocationDraft} busy={allocating} error={allocationError}
            onAdd={point => { setAllocationDraft(current => [...current, point]); setAllocationError(''); allocationRequest.current = null; }}
            onRemove={point => { setAllocationDraft(current => removeDraftAllocationPoint(current, point)); setAllocationError(''); allocationRequest.current = null; }}
            onCancel={() => { setAllocationDraft(current => current.slice(0, -1)); setAllocationError(''); allocationRequest.current = null; }}
            onSave={saveAllocation} />}
          currentGameMinute={minute} vitals={player} mode="play" onContinue={() => setSheetOpen(false)} />
      </div>
    </div>}
    {mapId && <PlaceMapModal isOpen onClose={() => setMapId('')} place={place} mapPlan={mapPlan} zoom={zoom}
      onZoomIn={() => setZoom(value => Math.min(256, value * 1.35))}
      onZoomOut={() => setZoom(value => Math.max(mapPlan?.playerCentered ? 0.0001 : 1, value / 1.35))}
      playerPortraitSrc={portrait.src} playerLabel={player.name} beingPortraitsById={npcPortraits} placeLabelFor={placeLabel}
      placeTooltipFor={placeLabel} legend={<span aria-label="Map legend" style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 12 }}>
        {Object.entries(mapStyles).map(([kind, style]) => <span key={kind} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span aria-hidden="true" style={{ width: 14, borderTop: `3px solid ${style.color}` }} />{style.label}
        </span>)}
        <span style={{ color: '#fb923c' }}>Amber: recent movement →</span>
        <span style={{ color: '#b8b8b8' }}>Gray: last seen</span>
      </span>} />}
  </main>;
}
